/**
 * Marketing attribution as it arrives from the API.
 *
 * Derived server-side at the BFF, never in the browser, so these are read-only
 * here. `source`, `channel` and `typeOfStay` answer separate questions and live
 * on the booking and reservation models directly.
 */
export enum MarketingChannel {
  GOOGLE = 'google',
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  TIKTOK = 'tiktok',
  LINKEDIN = 'linkedin',
  EMAIL = 'email',
  BLOG = 'blog',
  REFERRAL = 'referral',
  // Where the guest came FROM: a link shared into a WhatsApp chat, group or
  // broadcast. Distinct from `MessagingChannel.WHATSAPP`, which is where they
  // talked to us -- a booking can honestly carry both.
  WHATSAPP = 'whatsapp',
  DIRECT = 'direct',
  ORGANIC_SEARCH = 'organic_search',
  OTA = 'ota',
  UNKNOWN = 'unknown'
}

export enum MessagingChannel {
  WHATSAPP = 'whatsapp',
  INSTAGRAM_DM = 'instagram_dm',
  FACEBOOK_MESSENGER = 'facebook_messenger',
  // The chat widget on our own site -- the guest messaged us without ever
  // leaving the page.
  CHAT_BUBBLE = 'chat_bubble',
  PHONE_CALL = 'phone_call',
  NONE = 'none'
}

export enum AdType {
  PAID = 'paid',
  ORGANIC = 'organic',
  UNKNOWN = 'unknown'
}

export interface Attribution {
  campaign_source?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_campaign_id?: string;
  utm_term?: string;
  utm_term_id?: string;
  utm_content?: string;
  utm_content_id?: string;
  fbclid?: string;
  gclid?: string;
  ttclid?: string;
  fbc?: string;
  fbp?: string;
  landingPath?: string;
  referrer?: string;
  attributedAt?: number;
  marketingChannel?: MarketingChannel;
  messagingChannel?: MessagingChannel;
  adType?: AdType;
}

export const ATTRIBUTION_FIELDS: (keyof Attribution)[] = [
  'campaign_source',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_campaign_id',
  'utm_term',
  'utm_term_id',
  'utm_content',
  'utm_content_id',
  'fbclid',
  'gclid',
  'ttclid',
  'fbc',
  'fbp',
  'landingPath',
  'referrer',
  'attributedAt',
  'marketingChannel',
  'messagingChannel',
  'adType'
];

/** Human labels for the values a report or a table cell shows. */
export const marketingChannelLabels: Record<string, string> = {
  [MarketingChannel.GOOGLE]: 'Google',
  [MarketingChannel.FACEBOOK]: 'Facebook',
  [MarketingChannel.INSTAGRAM]: 'Instagram',
  [MarketingChannel.TIKTOK]: 'TikTok',
  [MarketingChannel.LINKEDIN]: 'LinkedIn',
  [MarketingChannel.EMAIL]: 'Email',
  [MarketingChannel.BLOG]: 'Blog',
  [MarketingChannel.REFERRAL]: 'Referral',
  [MarketingChannel.WHATSAPP]: 'WhatsApp',
  [MarketingChannel.DIRECT]: 'Direct',
  [MarketingChannel.ORGANIC_SEARCH]: 'Organic search',
  [MarketingChannel.OTA]: 'OTA',
  [MarketingChannel.UNKNOWN]: 'Unknown'
};

export const adTypeLabels: Record<string, string> = {
  [AdType.PAID]: 'Paid',
  [AdType.ORGANIC]: 'Organic',
  [AdType.UNKNOWN]: 'Unknown'
};

export const messagingChannelLabels: Record<string, string> = {
  [MessagingChannel.WHATSAPP]: 'WhatsApp',
  [MessagingChannel.INSTAGRAM_DM]: 'Instagram DM',
  [MessagingChannel.FACEBOOK_MESSENGER]: 'Messenger',
  [MessagingChannel.CHAT_BUBBLE]: 'Chat bubble',
  [MessagingChannel.PHONE_CALL]: 'Phone call',
  [MessagingChannel.NONE]: '—'
};

/** `unknown` means we could not determine it, which is different from a value
 *  simply being absent. Both read as "—" but only one is a real answer. */
export const getMarketingChannelLabel = (value?: string): string =>
  value ? marketingChannelLabels[value] || value : '—';

export const getAdTypeLabel = (value?: string): string =>
  value ? adTypeLabels[value] || value : '—';

export type AttributionOption<T> = { value: T; label: string };

// Shared by the create form and the source/channel modal on the reservation
// page, so the two offer the same answers to the same question. They were a
// local const in the create container until the modal needed them too.
export const marketingChannelOptions: AttributionOption<MarketingChannel>[] = Object.values(
  MarketingChannel
).map((value) => ({ value, label: marketingChannelLabels[value] }));

// `NONE` renders as an em dash wherever a reservation is displayed, where a
// blank reads as missing data. In a picker it is something someone chooses --
// the guest never messaged us -- so it gets a word.
export const messagingChannelOptions: AttributionOption<MessagingChannel>[] = Object.values(
  MessagingChannel
).map((value) => ({
  value,
  label: value === MessagingChannel.NONE ? 'None' : messagingChannelLabels[value]
}));
